import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { formatBangkokDateTime } from './bangkokTime';

const money = (v) => Number(v || 0).toFixed(2);
const upper = (v) => String(v || '-').toUpperCase();
const dt = (v) => (v ? formatBangkokDateTime(v) : '-');

const rowStatus = (row) => {
  if (row.is_amount_matched && row.is_codes_matched) return 'Matched';
  if (!row.is_amount_matched && !row.is_codes_matched) return 'Mismatch';
  return 'Partial';
};

// Builds every table of the report once so PDF and Excel stay identical.
const buildSections = ({ summary, daily, paymentBreakdown, paymentDaily, unscanned, scanned }) => {
  const paymentHead = ['Payment', 'Bills', 'Billed Amount', 'Codes', 'Scanned Codes', 'Unscanned Codes', 'Scanned Amount', 'Unscanned Amount'];
  const paymentRow = (r) => [upper(r.payment_method), r.bills_count, money(r.billed_amount), r.generated_codes, r.scanned_codes, r.unscanned_codes, money(r.scanned_amount), money(r.unscanned_amount)];
  const codeHead = ['Bill Number', 'Ticket ID', 'Barcode', 'Type', 'Payment', 'Generated On', 'Amount', 'Cashier'];
  const codeRow = (r) => [r.bill_number, r.ticket_id, r.barcode_value, r.print_mode, upper(r.payment_method), dt(r.created_at), money(r.ticket_amount), r.cashier_name || '-'];

  return [
    {
      name: 'Summary',
      title: 'Summary',
      head: ['Metric', 'Value'],
      body: [
        ['Billed Amount', money(summary.billed_amount)],
        ['Scanned Amount', money(summary.scanned_amount)],
        ['Amount Difference', money(summary.amount_difference)],
        ['Bills', summary.bills_count || 0],
        ['Generated Codes', summary.generated_codes || 0],
        ['Unique Scanned Codes', summary.unique_scanned_codes || 0],
        ['Generated but Unscanned', summary.unscanned_codes || 0],
        ['Verified Scans', summary.verified_scans || 0],
        ['Duplicate Scans', summary.duplicate_scans || 0],
        ['Not Found Scans', summary.not_found_scans || 0],
      ],
    },
    {
      name: 'Payment-wise',
      title: 'Payment-wise Reconciliation',
      head: paymentHead,
      body: paymentBreakdown.map(paymentRow),
    },
    {
      name: 'Payment Per Day',
      title: 'Payment-wise Reconciliation (Per Day)',
      head: ['Date', ...paymentHead],
      body: paymentDaily.map((r) => [r.report_date, ...paymentRow(r)]),
    },
    {
      name: 'Daily',
      title: 'Daily Reconciliation',
      head: ['Date', 'Bills', 'Billed Amount', 'Generated Codes', 'Unique Scanned', 'Scanned Amount', 'Amount Diff', 'Verified', 'Duplicate', 'Not Found', 'Status'],
      body: daily.map((r) => [r.report_date, r.bills_count, money(r.billed_amount), r.generated_codes, r.unique_scanned_codes, money(r.scanned_amount), money(r.amount_difference), r.verified_scans, r.duplicate_scans, r.not_found_scans, rowStatus(r)]),
    },
    {
      name: 'Unscanned',
      title: `Generated but not scanned (${unscanned.length})`,
      head: codeHead,
      body: unscanned.map(codeRow),
    },
    {
      name: 'Scanned',
      title: `Scanned codes (${scanned.length})`,
      head: [...codeHead, 'Scanned At', 'Scanned By'],
      body: scanned.map((r) => [...codeRow(r), dt(r.verified_at), r.verified_by || '-']),
    },
  ];
};

const rangeLabel = (dateRange) => {
  const [start, end] = Array.isArray(dateRange) ? dateRange : [];
  if (!start || !end) return 'all';
  const s = start.format('YYYY-MM-DD');
  const e = end.format('YYYY-MM-DD');
  return s === e ? s : `${s}_to_${e}`;
};

export const exportReconciliationExcel = (data, dateRange) => {
  const wb = XLSX.utils.book_new();
  buildSections(data).forEach((section) => {
    const ws = XLSX.utils.aoa_to_sheet([section.head, ...section.body]);
    ws['!cols'] = section.head.map((h, i) => ({
      wch: Math.min(40, Math.max(String(h).length, ...section.body.map((r) => String(r[i] ?? '').length)) + 2),
    }));
    XLSX.utils.book_append_sheet(wb, ws, section.name);
  });
  XLSX.writeFile(wb, `Ticket_Reconciliation_${rangeLabel(dateRange)}.xlsx`);
};

export const exportReconciliationPdf = (data, dateRange) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const label = rangeLabel(dateRange).replace('_to_', ' to ');
  let y = 40;

  doc.setFontSize(16);
  doc.text('Ticket Reconciliation Report', 40, y);
  doc.setFontSize(10);
  doc.text(`Period: ${label}   |   Generated: ${formatBangkokDateTime(new Date())} (Bangkok)`, 40, y + 16);
  y += 34;

  buildSections(data).forEach((section) => {
    if (y > 450) {
      doc.addPage();
      y = 40;
    }
    doc.setFontSize(12);
    doc.text(section.title, 40, y);
    doc.autoTable({
      startY: y + 6,
      head: [section.head],
      body: section.body.length ? section.body : [[{ content: 'No records', colSpan: section.head.length, styles: { halign: 'center' } }]],
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [31, 36, 48] },
      margin: { left: 40, right: 40, bottom: 50 },
    });
    y = doc.lastAutoTable.finalY + 24;
  });

  // Footer on every page: credit line + page number.
  const pageCount = doc.internal.getNumberOfPages();
  const { width, height } = doc.internal.pageSize;
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(200);
    doc.line(40, height - 36, width - 40, height - 36);
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text('Designed and developed by Cloudnet Software', width / 2, height - 22, { align: 'center' });
    doc.text(`Page ${page} of ${pageCount}`, width - 40, height - 22, { align: 'right' });
  }

  doc.save(`Ticket_Reconciliation_${rangeLabel(dateRange)}.pdf`);
};
